import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useApi } from '../context';
import { dayRangeParams, localISODate } from '../dates';
import { useCallEvents } from '../realtime';
import { CALL_STATE_LABEL, type CallLine, type CallRow, type CallState } from '../types';

const todayISO = localISODate;

/** "12s" / "1m 05s" between ringing and answer, or null when unanswered. */
function timeToAnswer(r: CallRow): string | null {
  if (!r.answered_at) return null;
  const secs = Math.max(0, Math.round((new Date(r.answered_at).getTime() - new Date(r.ringing_at).getTime()) / 1000));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
}

/** Calls for one day (today by default), filterable by number, live-updating. */
export function CallsPage() {
  const api = useApi();
  const [date, setDate] = useState(todayISO());
  const [number, setNumber] = useState('');
  const [state, setState] = useState<CallState | ''>('');
  const [lineNumber, setLineNumber] = useState('');
  const [lines, setLines] = useState<CallLine[]>([]);
  const [rows, setRows] = useState<CallRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const params = dayRangeParams(date);
    params.set('limit', '500');
    if (number) params.set('number', number);
    if (state) params.set('state', state);
    if (lineNumber) params.set('lineNumber', lineNumber);
    api.get<CallRow[]>(`/api/calls?${params}`).then(setRows, (e: Error) => setError(e.message));
  }, [api, date, number, state, lineNumber]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { api.get<CallLine[]>('/api/calls/lines').then(setLines, () => undefined); }, [api]);

  useCallEvents(useCallback(() => load(), [load]));

  const ringing = rows.filter((r) => r.state === 'ringing');
  const rest = rows.filter((r) => r.state !== 'ringing');
  const isToday = date === todayISO();

  return (
    <>
      <h1>Calls {isToday ? '— today' : `— ${date}`}</h1>
      <div className="filters">
        <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value || todayISO())} />
        {!isToday && <button onClick={() => setDate(todayISO())}>Today</button>}
        <input placeholder="Filter by number" value={number} onChange={(e) => setNumber(e.target.value)} />
        <select value={state} onChange={(e) => setState(e.target.value as CallState | '')}>
          <option value="">All states</option>
          <option value="ringing">Ringing</option>
          <option value="answered">Answered</option>
          <option value="in_progress">In progress</option>
          <option value="missed">Missed</option>
        </select>
        <select value={lineNumber} onChange={(e) => setLineNumber(e.target.value)} title="Filter by the line the call came in on">
          <option value="">All lines</option>
          {lines.map((l) => <option key={l.number} value={l.number}>{l.name} ({l.number})</option>)}
        </select>
        <button onClick={load}>Refresh</button>
      </div>
      {error && <p className="error">{error}</p>}
      <table>
        <thead>
          <tr>
            <th>Time</th><th>Number</th><th>Line called</th><th>Team</th><th>State</th><th>Answered by</th><th>Time to answer</th><th>Answered in</th><th>Type</th><th>Completed</th>
          </tr>
        </thead>
        <tbody>
          {[...ringing, ...rest].map((r) => (
            <tr key={r.id} className={r.state}>
              <td><Link to={`/calls/${r.id}`}>{new Date(r.ringing_at).toLocaleTimeString()}</Link></td>
              <td>{r.caller_number ?? '—'}</td>
              <td>
                {r.originating_queue_name ?? '—'}
                {r.originating_queue_number && <div className="sub">{r.originating_queue_number}</div>}
              </td>
              <td>{r.team_name ?? '—'}</td>
              <td><span className={`badge ${r.state}`}>{CALL_STATE_LABEL[r.state]}</span>{r.has_voicemail && <span title="Voicemail"> ☎️</span>}</td>
              <td>{r.answered_by ?? '—'}</td>
              <td>{timeToAnswer(r) ?? '—'}</td>
              <td>{r.answered_queue_name ?? '—'}</td>
              <td>{r.call_type ?? '—'}</td>
              <td>
                {r.completed_at
                  ? <span className="badge completed">✓ {new Date(r.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  : '—'}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9}>No calls on this day</td></tr>}
        </tbody>
      </table>
    </>
  );
}
